import { Component, input, signal } from '@angular/core';
import { UwIcon } from '../../ui/icon/uw-icon';
import { UwBadge } from '../../ui/badge/uw-badge';
import { UwButton } from '../../ui/button/uw-button';
import type { EnrichmentEvidence, EnrichmentRecord } from '../../../core/models/evidence.model';

@Component({
  selector: 'uw-enrichment-evidence',
  standalone: true,
  imports: [UwIcon, UwBadge, UwButton],
  template: `
    @if (enrichment(); as e) {
      <div class="space-y-3">
        <div class="flex items-center justify-between rounded-lg border border-line bg-surface p-3">
          <div class="flex items-center gap-2.5">
            <span class="flex h-8 w-8 items-center justify-center rounded-lg bg-info-soft text-info">
              <uw-icon name="database" [size]="16" />
            </span>
            <div>
              <p class="text-[12.5px] font-semibold text-primary">Data Enrichment & Matching</p>
              <p class="text-[10.5px] text-text-muted">{{ e.sourceLabel }}</p>
            </div>
          </div>
          <uw-badge [tone]="e.statusTone">{{ e.status }}</uw-badge>
        </div>

        <div class="space-y-2">
          @for (record of e.records; track record.attribute) {
            <div class="rounded-lg border border-line bg-surface p-3">
              <div class="flex items-center justify-between gap-2">
                <p class="text-[12px] font-semibold text-primary">{{ record.attribute }}</p>
                <uw-badge [tone]="resolved(record).tone">{{ resolved(record).label }}</uw-badge>
              </div>
              <div class="mt-2 grid grid-cols-1 gap-1.5 text-[11px] sm:grid-cols-2">
                <div class="rounded-md bg-surface-sunken px-2 py-1.5">
                  <p class="typo-label">Submitted</p>
                  <p class="mt-0.5 text-secondary">{{ record.submitted }}</p>
                </div>
                <div class="rounded-md bg-surface-sunken px-2 py-1.5">
                  <p class="typo-label">Enriched · {{ record.source }}</p>
                  <p class="mt-0.5 font-medium text-primary">{{ record.enriched }}</p>
                </div>
              </div>
              @if (record.reviewable) {
                <div class="mt-2 flex items-center justify-end gap-1.5">
                  @if (resolved(record).tone === 'warning') {
                    <uw-button variant="ghost" size="xs" icon="x" (click)="resolve(record, 'reject')">Reject</uw-button>
                    <uw-button variant="success" size="xs" icon="check" (click)="resolve(record, 'accept')">Accept</uw-button>
                  } @else {
                    <span class="text-[10.5px] text-text-muted">Auto-applied · logged to audit</span>
                  }
                </div>
              }
            </div>
          }
        </div>
      </div>
    }
  `,
})
export class UwEnrichmentEvidence {
  readonly enrichment = input<EnrichmentEvidence | null>(null);

  protected readonly resolvedMap = signal<Record<string, 'accepted' | 'rejected'>>({});

  protected resolved(record: EnrichmentRecord): { label: string; tone: 'success' | 'warning' | 'danger' } {
    const r = this.resolvedMap()[record.attribute];
    if (r === 'accepted') return { label: 'Accepted', tone: 'success' };
    if (r === 'rejected') return { label: 'Rejected', tone: 'danger' };
    if (record.statusTone === 'success') return { label: record.status, tone: 'success' };
    if (record.statusTone === 'warning') return { label: record.status, tone: 'warning' };
    return { label: record.status, tone: 'success' };
  }

  protected resolve(record: EnrichmentRecord, decision: 'accept' | 'reject'): void {
    this.resolvedMap.update((m) => ({ ...m, [record.attribute]: decision === 'accept' ? 'accepted' : 'rejected' }));
  }
}
